import { Download, Plus, Star } from "lucide-react";
import { useEffect, useState } from "react";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { DetailSection } from "@/components/shared/detail-section";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useSetFeedbackVisibility } from "@/hooks/use-review-feedback";
import {
  type ReviewReport as Report,
  useAddReviewer,
  useFinalizeManagerNote,
  useRegenerateReviewerQuestions,
  useReverseReviewSignOff,
  useReviewerMismatch,
  useReviewReport,
  useSaveManagerNoteDraft,
  useSetReviewerExcluded,
  useSetReviewerRelationship,
  useSignReviewOff,
} from "@/hooks/use-review-report";
import { useReviewRelationshipTypes } from "@/hooks/use-review-templates";
import { useSession } from "@/lib/auth-client";
import { categoryColorVar } from "@/lib/category-colors";
import { downloadCsv } from "@/lib/csv";
import { formatDateTime } from "@/lib/dates";
import { canEditAll } from "@/lib/permissions";
import {
  isManagerNoteSectionBlank,
  MANAGER_NOTE_SECTIONS,
} from "@/lib/reviews/manager-note-sections";
import { SCALE_NOT_APPLICABLE_ORDINAL } from "@/lib/reviews/scale";

/**
 * One person's review, read as a whole — EPIC-069.
 *
 * Built around the comparison, because that is what the conversation is about:
 * each criterion shows every reviewer's rating beside the others, with the
 * standard for that role and level underneath, and the comment that explains a
 * rating sitting with it rather than a click away.
 *
 * Where reviewers disagree, the page says so plainly. A gap is not a problem to
 * flag — it is the most useful thing on the page, and the thing a manager
 * should walk in ready to talk about.
 */
export function ReviewReport({ reviewId }: { reviewId: string }) {
  const { data, isLoading, isError, error, refetch } = useReviewReport(reviewId);

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Couldn't load this review"}
        onRetry={() => refetch()}
      />
    );
  }
  if (isLoading || !data) return <Skeleton className="h-96 w-full" />;

  // An excluded reviewer is no longer being asked, so they are neither
  // "waiting" nor part of "all in" — EPIC-057.
  const active = data.reviewers.filter((r) => !r.excludedAt);
  const waiting = active.filter((r) => r.statusKey !== "complete");
  /*
    Derived from the reviewers, not `review.status_key`. Closing a round stamps
    every review `closed` over `complete`, so reading the status badged a
    finished, closed review as "In progress" — wrong, and worse than saying
    nothing on the one screen a manager takes into the conversation.
  */
  const allIn = active.length > 0 && waiting.length === 0;
  const closed = data.cycleStatusKey === "closed";
  const status = allIn
    ? { tone: "success" as const, label: "All in" }
    : closed
      ? { tone: "warning" as const, label: `Closed — ${waiting.length} never came in` }
      : { tone: "info" as const, label: "In progress" };

  return (
    <div className="space-y-6">
      <DetailSection
        title="Review"
        headerActions={
          <div className="flex items-center gap-2">
            <StatusPill tone={status.tone} label={status.label} />
            <Button variant="outline" size="sm" onClick={() => exportReport(data)}>
              <Download className="size-4" aria-hidden />
              Export
            </Button>
          </div>
        }
      >
        {/*
          The subject sees themselves and their manager by name and everyone
          else pooled — `reviewAudienceFor` decides, and this page renders
          whatever it is handed rather than branching on a role, so there is no
          second place to get the rule wrong. Attribution fields are null only
          on rows the reader may not see individually, and pooled reviewers are
          not rows here at all: they arrive in `criterion.groups`.
        */}
        {/*
          Only Manager and Period reviewed here — role, level and round are
          already the page title/description (ReviewReportPage), so repeating
          them was noise rather than orientation.
        */}
        <dl className="grid gap-4 text-sm sm:grid-cols-2">
          <Fact label="Manager" value={data.managerName ?? "—"} />
          <Fact label="Period reviewed" value={`${data.periodStart} – ${data.periodEnd}`} />
        </dl>

        <div className="mt-5 border-t pt-4">
          <div className="flex items-center justify-between gap-2">
            <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
              Reviewers
            </p>
            <AddReviewerControl reviewId={data.reviewId} />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {data.reviewers.map((reviewer, index) => (
              <ReviewerPill
                key={reviewer.reviewerId ?? `reviewer-${index}`}
                reviewId={data.reviewId}
                reviewer={reviewer}
              />
            ))}
          </div>

          {waiting.length > 0 && (
            <p className="mt-3 text-muted-foreground text-sm">
              {/*
                Said rather than implied: a report read halfway through a round
                is a partial picture, and the reader should know which part.
              */}
              {waiting.every((r) => r.reviewerName)
                ? `Still waiting on ${waiting.map((r) => r.reviewerName).join(", ")}`
                : `Still waiting on ${waiting.length} ${waiting.length === 1 ? "reviewer" : "reviewers"}`}{" "}
              — what follows is only what has been handed in.
            </p>
          )}
        </div>
      </DetailSection>

      <ManagerNotes
        reviewId={data.reviewId}
        notes={data.managerNotes}
        canAddManagerNote={data.canAddManagerNote}
      />

      {data.criteria.map((criterion) => (
        <CriterionComparison key={criterion.questionId} criterion={criterion} />
      ))}

      <Endorsements notes={data.endorsements} />

      <WrittenFeedback
        reviewId={data.reviewId}
        subjectName={data.subjectName}
        notes={data.feedback}
        canShare={data.canShareFeedback}
      />

      <AdHocFeedbackSinceLastCycle notes={data.adHocFeedback} />

      {/* Last on the page, deliberately: signing off is the conversation's
          conclusion, not a step to take before reading the rest. */}
      <SignOff report={data} />
    </div>
  );
}

/**
 * Add a reviewer by hand — EPIC-059 WI-212/WI-211. HR-only: same
 * `canEditAll` gate every other roster action on this page already uses.
 * Today this is also the only way a PEER reviewer is assigned at all —
 * automatic peer derivation from shared project staffing (EPIC-056) hasn't
 * landed yet.
 */
function AddReviewerControl({ reviewId }: { reviewId: string }) {
  const { data: session } = useSession();
  const isEditAdmin = canEditAll(session?.user?.role);
  const { data: relationshipTypes } = useReviewRelationshipTypes();
  const addReviewer = useAddReviewer(reviewId);
  const [open, setOpen] = useState(false);
  const [personId, setPersonId] = useState<string | null>(null);
  const [relationshipKey, setRelationshipKey] = useState<string | null>(null);

  if (!isEditAdmin) return null;

  const reset = () => {
    setPersonId(null);
    setRelationshipKey(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 text-xs">
          <Plus className="size-3.5" aria-hidden />
          Add a reviewer
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a reviewer</DialogTitle>
          <DialogDescription>
            Seeds their questions the same way the round would have, so their task is complete and
            ready to answer right away.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">Person</Label>
            <EntityCombobox
              entity="persons"
              value={personId}
              onChange={setPersonId}
              placeholder="Choose a person…"
            />
          </div>
          <div>
            <Label className="mb-1.5 block" htmlFor="add-reviewer-relationship">
              Relationship
            </Label>
            <Select value={relationshipKey ?? undefined} onValueChange={setRelationshipKey}>
              <SelectTrigger id="add-reviewer-relationship">
                <SelectValue placeholder="Choose a relationship…" />
              </SelectTrigger>
              <SelectContent>
                {(relationshipTypes ?? []).map((type) => (
                  <SelectItem key={type.key} value={type.key}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={!personId || !relationshipKey || addReviewer.isPending}
            onClick={() => {
              if (!personId || !relationshipKey) return;
              addReviewer.mutate(
                { reviewerPersonId: personId, relationshipKey },
                { onSuccess: () => setOpen(false) },
              );
            }}
          >
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One reviewer: the exclude/include toggle (EPIC-057), and — for an edit
 * admin — the relationship it was assigned under and the mismatch warning
 * that follows a change (EPIC-059 WI-213).
 *
 * `reviewer.excludedAt` only ever arrives non-null on a full reader's
 * payload; the server drops excluded rows entirely from the subject's
 * redacted view, so there is nothing to gate here beyond who may ACT —
 * `canEditAll`, the same check the sign-off reversal button uses.
 */
function ReviewerPill({
  reviewId,
  reviewer,
}: {
  reviewId: string;
  reviewer: Report["reviewers"][number];
}) {
  const { data: session } = useSession();
  const isEditAdmin = canEditAll(session?.user?.role);
  const setExcluded = useSetReviewerExcluded(reviewId);
  const setRelationship = useSetReviewerRelationship(reviewId);
  const regenerate = useRegenerateReviewerQuestions(reviewId);
  const { data: relationshipTypes } = useReviewRelationshipTypes();
  const { data: mismatch } = useReviewerMismatch(reviewId, reviewer.reviewerId, isEditAdmin);
  const excluded = Boolean(reviewer.excludedAt);
  const name =
    reviewer.relationshipName && reviewer.reviewerName
      ? `${reviewer.relationshipName}: ${reviewer.reviewerName}`
      : "Reviewer";

  return (
    <div className="flex items-center gap-1">
      <StatusPill
        tone={excluded ? "warning" : reviewer.statusKey === "complete" ? "success" : "neutral"}
        label={`${name}${excluded ? " — excluded" : reviewer.statusKey === "complete" ? "" : " — not in yet"}`}
      />
      {isEditAdmin && reviewer.reviewerId && (
        <>
          <Select
            value={reviewer.relationshipKey ?? undefined}
            onValueChange={(next) =>
              reviewer.reviewerId &&
              setRelationship.mutate({ reviewerId: reviewer.reviewerId, relationshipKey: next })
            }
          >
            <SelectTrigger
              className="h-6 w-auto gap-1 border-none bg-transparent px-1.5 text-muted-foreground text-xs shadow-none"
              aria-label="Change this reviewer's relationship"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(relationshipTypes ?? []).map((type) => (
                <SelectItem key={type.key} value={type.key}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {mismatch?.hasMismatch && (
            <ConfirmDialog
              title="Regenerate this reviewer's questions?"
              description={`Their seeded questions are from before this relationship changed. Regenerating asks the ${mismatch.newQuestionCount} ${mismatch.newQuestionCount === 1 ? "question" : "questions"} for ${mismatch.currentRelationshipName}${mismatch.answeredToDiscard > 0 ? ` — ${mismatch.answeredToDiscard} already-answered ${mismatch.answeredToDiscard === 1 ? "response" : "responses"} without a matching competency will be discarded` : ""}.`}
              confirmLabel="Regenerate"
              onConfirm={() => reviewer.reviewerId && regenerate.mutate(reviewer.reviewerId)}
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-1.5 text-warning-foreground text-xs"
                  disabled={regenerate.isPending}
                >
                  Questions don't match — regenerate
                </Button>
              }
            />
          )}
          <ConfirmDialog
            title={excluded ? "Include this reviewer again?" : "Exclude this reviewer?"}
            description={
              excluded
                ? "They're asked to take part in this review again."
                : "Takes them off this review. Their answers are kept, and this can be undone."
            }
            confirmLabel={excluded ? "Include" : "Exclude"}
            onConfirm={() =>
              reviewer.reviewerId &&
              setExcluded.mutate({ reviewerId: reviewer.reviewerId, excluded: !excluded })
            }
            trigger={
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-1.5 text-muted-foreground text-xs"
                disabled={setExcluded.isPending}
              >
                {excluded ? "Include" : "Exclude"}
              </Button>
            }
          />
        </>
      )}
    </div>
  );
}

/**
 * The formal record that the conversation happened — EPIC-065.
 *
 * Two independent lines: either party may sign first, and neither waits on
 * the other. Nothing renders for a party that isn't eligible yet or isn't the
 * one looking — `canSignAsManager`/`canSignAsEmployee` already fold in both
 * checks, so this component never re-derives who may act.
 */
function SignOff({ report }: { report: Report }) {
  const { data: session } = useSession();
  const isEditAdmin = canEditAll(session?.user?.role);
  const signAsManager = useSignReviewOff(report.reviewId, "manager");
  const signAsEmployee = useSignReviewOff(report.reviewId, "employee");
  const reverse = useReverseReviewSignOff(report.reviewId);

  const rows: {
    party: "manager" | "employee";
    label: string;
    signedByName: string | null;
    signedAt: string | null;
    canSign: boolean;
    sign: () => void;
    pending: boolean;
  }[] = [
    {
      party: "manager",
      label: "Manager",
      signedByName: report.managerSignedByName,
      signedAt: report.managerSignedAt,
      canSign: report.canSignAsManager,
      sign: () => signAsManager.mutate(),
      pending: signAsManager.isPending,
    },
    {
      party: "employee",
      label: "Employee",
      signedByName: report.employeeSignedByName,
      signedAt: report.employeeSignedAt,
      canSign: report.canSignAsEmployee,
      sign: () => signAsEmployee.mutate(),
      pending: signAsEmployee.isPending,
    },
  ];

  // A row renders when it's already signed (visible to anyone entitled to
  // the report) or when THIS viewer may sign it — never for a party neither
  // signed nor actionable by this reader.
  const visibleRows = rows.filter((row) => row.signedAt || row.canSign);
  if (visibleRows.length === 0) return null;

  return (
    <DetailSection title="Sign-off" boldTitle className="border-2 border-primary/40 bg-primary/5">
      <ul className="grid gap-3 sm:grid-cols-2">
        {visibleRows.map((row) => (
          <li key={row.party} className="rounded-lg border border-border p-3">
            <p className="font-medium text-sm">{row.label}</p>
            {row.signedAt ? (
              <div className="mt-1 flex flex-wrap items-center justify-between gap-2">
                <StatusPill
                  tone="success"
                  label={`Signed by ${row.signedByName ?? "—"} on ${formatDateTime(row.signedAt)}`}
                />
                {isEditAdmin && (
                  <ConfirmDialog
                    title={`Reverse ${row.label.toLowerCase()} sign-off?`}
                    description={`This removes ${row.signedByName ?? "their"} sign-off. It will be recorded.`}
                    confirmLabel="Reverse"
                    destructive
                    onConfirm={() => reverse.mutate(row.party)}
                    trigger={
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        disabled={reverse.isPending}
                      >
                        Reverse
                      </Button>
                    }
                  />
                )}
              </div>
            ) : row.canSign ? (
              <ConfirmDialog
                title="Confirm this review happened?"
                description="This confirms the review conversation took place — not that you agree with everything in it."
                confirmLabel="Sign off"
                onConfirm={row.sign}
                trigger={
                  <Button className="mt-2 w-full" disabled={row.pending}>
                    Sign off
                  </Button>
                }
              />
            ) : (
              <p className="mt-1 text-muted-foreground text-sm">Not yet signed</p>
            )}
          </li>
        ))}
      </ul>
    </DetailSection>
  );
}

/**
 * The manager's own prep for the conversation — EPIC-064, shown as "Summary
 * and Next Steps" (AC-672).
 *
 * `canAddManagerNote` is carried separately from `managerNotes` for exactly
 * one reason: a manager who hasn't written a note yet has no row in
 * `managerNotes` to find, and without a dedicated flag this component had no
 * way to tell "nobody has written one" apart from "you may not write one" —
 * which is the bug that shipped first (no entry point at all for a
 * never-started note). `isMine` on an existing row still answers "is this
 * editable" once one exists.
 */
/** One author's note, read-only — their own finalized note, or someone else's. */
function ManagerNoteSections({ note }: { note: Report["managerNotes"][number] }) {
  return (
    <div className="space-y-3">
      {MANAGER_NOTE_SECTIONS.filter((section) => !isManagerNoteSectionBlank(note[section.key])).map(
        (section) => (
          <div key={section.key}>
            <p className="font-medium text-muted-foreground text-xs">{section.label}</p>
            <p className="whitespace-pre-line text-sm">{note[section.key]}</p>
          </div>
        ),
      )}
    </div>
  );
}

function ManagerNotes({
  reviewId,
  notes,
  canAddManagerNote,
}: {
  reviewId: string;
  notes: Report["managerNotes"];
  canAddManagerNote: boolean;
}) {
  const mine = notes.find((n) => n.isMine);
  const others = notes.filter((n) => !n.isMine);
  const [keepDoing, setKeepDoing] = useState(mine?.keepDoing ?? "");
  const [improve, setImprove] = useState(mine?.improve ?? "");
  const [goalsNextSteps, setGoalsNextSteps] = useState(mine?.goalsNextSteps ?? "");
  const saveDraft = useSaveManagerNoteDraft(reviewId);
  const finalize = useFinalizeManagerNote(reviewId);

  useEffect(() => {
    setKeepDoing(mine?.keepDoing ?? "");
    setImprove(mine?.improve ?? "");
    setGoalsNextSteps(mine?.goalsNextSteps ?? "");
  }, [mine?.keepDoing, mine?.improve, mine?.goalsNextSteps]);

  // Nothing to show: this viewer may not write one, there's no draft of
  // theirs sitting around (e.g. the round has since closed), and nobody
  // else's is finalized yet.
  if (!canAddManagerNote && !mine && others.length === 0) return null;

  const draftValues = { keepDoing, improve, goalsNextSteps };
  // Finalize always saves first: a manager may type all three sections and
  // click Finalize without ever clicking "Save draft" — without this there is
  // no row yet for the server to finalize, and it fails with "there is no
  // draft yet to finalize."
  const finalizeAfterSaving = async () => {
    try {
      await saveDraft.mutateAsync(draftValues);
    } catch {
      return;
    }
    finalize.mutate();
  };
  const allBlank =
    isManagerNoteSectionBlank(keepDoing) &&
    isManagerNoteSectionBlank(improve) &&
    isManagerNoteSectionBlank(goalsNextSteps);
  const anyBlank =
    isManagerNoteSectionBlank(keepDoing) ||
    isManagerNoteSectionBlank(improve) ||
    isManagerNoteSectionBlank(goalsNextSteps);

  return (
    <DetailSection title="Summary and Next Steps">
      <div className="space-y-4">
        {canAddManagerNote ? (
          <div className="space-y-3">
            <div>
              <Label htmlFor="manager-note-keep-doing">Things to keep doing</Label>
              <Textarea
                id="manager-note-keep-doing"
                value={keepDoing}
                onChange={(e) => setKeepDoing(e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="manager-note-improve">Things to improve</Label>
              <Textarea
                id="manager-note-improve"
                value={improve}
                onChange={(e) => setImprove(e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="manager-note-goals">Goals and next steps</Label>
              <Textarea
                id="manager-note-goals"
                value={goalsNextSteps}
                onChange={(e) => setGoalsNextSteps(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={saveDraft.isPending || allBlank}
                onClick={() => saveDraft.mutate(draftValues)}
              >
                Save draft
              </Button>
              {!mine || mine.isDraft ? (
                <ConfirmDialog
                  title="Finalize this note?"
                  description="Other people with access to this review will be able to read this. You can still fix typos afterward, but this isn't a draft anymore."
                  confirmLabel="Finalize"
                  onConfirm={finalizeAfterSaving}
                  trigger={
                    <Button
                      size="sm"
                      disabled={saveDraft.isPending || finalize.isPending || anyBlank}
                    >
                      Finalize
                    </Button>
                  }
                />
              ) : (
                <StatusPill tone="success" label="Finalized" />
              )}
            </div>
          </div>
        ) : (
          mine && (
            <div>
              <ManagerNoteSections note={mine} />
              <p className="mt-2 text-muted-foreground text-xs">
                {mine.isDraft ? "Draft" : "Finalized"} · {formatDateTime(mine.updatedAt)}
              </p>
            </div>
          )
        )}
        {others.map((note) => (
          <div key={note.id} className="rounded-lg border border-border p-3">
            <ManagerNoteSections note={note} />
            <p className="mt-2 text-muted-foreground text-xs">
              {note.authorName} · {formatDateTime(note.updatedAt)}
            </p>
          </div>
        ))}
      </div>
    </DetailSection>
  );
}

/**
 * The written half of the review — EPIC-063.
 *
 * Who is looking decides what arrives: a full reader gets every note, and the
 * subject gets only the ones a manager ticked through. That filtering happens
 * in `getReviewReport`, so this component renders what it is given and never
 * asks who the reader is — the share control simply is not on a subject's
 * payload, because `isVisibleToEmployee` is true on every note they receive.
 *
 * The distinction that matters on screen: a note with no author is anonymous
 * and always was. It is not a name being withheld from this reader, and the
 * label says "Unsigned" rather than "Anonymous reviewer" so it does not read
 * as a person whose name is being kept back.
 */
/**
 * What people singled out — EPIC-069 WI-254.
 *
 * Shown to everyone entitled to the review, the subject included and without
 * a release step: this is praise, and withholding praise needs a reason
 * nobody has offered. Attribution is already resolved by `getReviewReport`,
 * so a name here means the reader is entitled to it.
 */
function Endorsements({ notes }: { notes: Report["endorsements"] }) {
  if (notes.length === 0) return null;
  return (
    <DetailSection
      title="Endorsements"
      headerActions={<StatusPill tone="info" label={`${notes.length}`} />}
    >
      <ul className="space-y-2">
        {notes.map((note) => (
          <li key={note.id} className="rounded-lg border border-border p-3">
            <span className="flex items-center gap-2 font-medium text-sm">
              <Star className="size-4 text-muted-foreground" aria-hidden />
              {note.title}
            </span>
            <p className="mt-1 whitespace-pre-line text-sm">{note.detail}</p>
            <p className="mt-1 text-2xs text-muted-foreground">
              {note.endorserName
                ? `${note.relationshipName} · ${note.endorserName}`
                : (note.relationshipName ?? "A reviewer")}
            </p>
          </li>
        ))}
      </ul>
    </DetailSection>
  );
}

/**
 * Standalone feedback given about this subject since their previous cycle
 * closed — EPIC-067 WI-247. Renders nothing until there is any, same posture
 * as the other optional sections on this page. Deliberately separate from
 * `WrittenFeedback` above (AC-706) and never folds into the ratings or
 * scores on this review (AC-707) — context only, for the conversation.
 */
function AdHocFeedbackSinceLastCycle({ notes }: { notes: Report["adHocFeedback"] }) {
  if (notes.length === 0) return null;

  return (
    <DetailSection title="Feedback given since the last cycle">
      <p className="mb-3 text-muted-foreground text-xs">
        Given outside this round — not part of its ratings.
      </p>
      <ul className="space-y-2">
        {notes.map((note) => (
          <li key={note.id} className="rounded-lg border border-border p-3">
            {note.relatedProjectName && (
              <StatusPill tone="neutral" label={note.relatedProjectName} className="mb-1.5" />
            )}
            <p className="whitespace-pre-line text-sm">{note.body}</p>
            <p className="mt-2 text-muted-foreground text-xs">
              {note.isAnonymous ? "Unsigned" : (note.authorName ?? "A colleague")} ·{" "}
              {note.sentimentName}
            </p>
          </li>
        ))}
      </ul>
    </DetailSection>
  );
}

function WrittenFeedback({
  reviewId,
  subjectName,
  notes,
  canShare,
}: {
  reviewId: string;
  subjectName: string;
  notes: Report["feedback"];
  /** Decided by the server (`canShareFeedback`), which also enforces it. */
  canShare: boolean;
}) {
  const setVisibility = useSetFeedbackVisibility(reviewId);
  if (notes.length === 0) return null;

  const byTag = new Map<string, Report["feedback"]>();
  for (const note of notes) {
    const forTag = byTag.get(note.sentimentName);
    if (forTag) forTag.push(note);
    else byTag.set(note.sentimentName, [note]);
  }

  return (
    <DetailSection title="Written feedback">
      <div className="space-y-6">
        {[...byTag.entries()].map(([tagName, group]) => (
          <div key={tagName} className="space-y-2">
            <StatusPill tone="neutral" label={tagName} />
            <ul className="space-y-2">
              {group.map((note) => (
                <li key={note.id} className="rounded-lg border border-border p-3">
                  <p className="whitespace-pre-line text-sm">{note.body}</p>
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <span className="text-muted-foreground text-xs">
                      {note.isAnonymous
                        ? "Unsigned"
                        : note.authorName
                          ? `${note.relationshipName} · ${note.authorName}`
                          : (note.relationshipName ?? "Reviewer")}
                    </span>
                    {canShare && (
                      <Label
                        htmlFor={`share-${note.id}`}
                        className="flex items-center gap-2 font-normal text-xs"
                      >
                        <Checkbox
                          id={`share-${note.id}`}
                          checked={note.isVisibleToEmployee}
                          disabled={setVisibility.isPending}
                          onCheckedChange={(checked) =>
                            setVisibility.mutate({
                              feedbackId: note.id,
                              isVisibleToEmployee: checked === true,
                            })
                          }
                        />
                        Share with {subjectName.split(" ")[0] ?? "them"}
                      </Label>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {canShare && (
        <p className="mt-4 text-muted-foreground text-sm">
          {/*
            Said rather than implied: nothing written reaches the person being
            reviewed unless somebody ticks it, and the rest is for the
            conversation.
          */}
          {subjectName.split(" ")[0] ?? "They"} sees only the notes you tick. The rest are yours to
          talk through.
        </p>
      )}
    </DetailSection>
  );
}

function CriterionComparison({ criterion }: { criterion: Report["criteria"][number] }) {
  const rated = criterion.answers.filter((a) => a.ordinal !== null);
  // N/A (6) is an answer, not a score, so it never counts as a disagreement.
  const scored = rated.filter((a) => a.ordinal !== null && a.ordinal < 6);
  /**
   * A pooled group's average counts toward the spread as one voice.
   *
   * Leaving groups out would tell the subject "everyone agreed" when their
   * self-score and their manager's matched and the peers were a point below,
   * which is the disagreement most worth noticing. Using the average rather
   * than the individual peer scores is deliberate: the spread WITHIN a pool is
   * exactly what pooling exists to hide.
   */
  const ordinals = [
    ...scored.map((a) => a.ordinal ?? 0),
    ...criterion.groups.map((g) => g.averageOrdinal).filter((avg): avg is number => avg !== null),
  ];
  const gap = ordinals.length > 1 ? Math.max(...ordinals) - Math.min(...ordinals) : 0;

  // Everyone on the line, each in their own colour, so the comparison is
  // seen before it is read.
  const markers: ScaleMarker[] = [
    ...criterion.answers.flatMap((answer, index) =>
      answer.ordinal !== null && answer.ordinal < SCALE_NOT_APPLICABLE_ORDINAL
        ? [
            {
              key: answer.reviewerId ?? `answer-${index}`,
              label: answer.relationshipName ?? "Rating",
              short: (answer.relationshipName ?? "R").charAt(0).toUpperCase(),
              at: answer.ordinal,
              color: relationshipColor(answer.relationshipKey, index),
            },
          ]
        : [],
    ),
    ...criterion.groups.flatMap((group, index) =>
      group.averageOrdinal !== null
        ? [
            {
              key: group.relationshipKey,
              label: `${group.relationshipName} (average)`,
              short: group.relationshipName.charAt(0).toUpperCase(),
              at: group.averageOrdinal,
              color: relationshipColor(group.relationshipKey, criterion.answers.length + index),
            },
          ]
        : [],
    ),
  ];

  return (
    <DetailSection
      title={criterion.name}
      boldTitle
      headerActions={
        <div className="flex items-center gap-2">
          {criterion.categoryName && (
            <span className="flex items-center gap-1.5 text-muted-foreground text-sm">
              {categoryColorVar(criterion.categoryColor) && (
                <span
                  className="size-2.5 rounded-full"
                  style={{
                    backgroundColor: categoryColorVar(criterion.categoryColor) ?? undefined,
                  }}
                  aria-hidden
                />
              )}
              {criterion.categoryName}
            </span>
          )}
          {/*
            The gap is the point of the page, so it is stated rather than left
            to be worked out by reading across.
          */}
          {gap > 0 ? (
            <StatusPill
              tone={gap > 1 ? "warning" : "info"}
              label={`${gap > 1 ? "Views differ" : "Slight difference"} · ${formatGap(gap)}`}
            />
          ) : (
            ordinals.length > 1 && <StatusPill tone="success" label="In agreement" />
          )}
        </div>
      }
    >
      {criterion.meetsExpectation && (
        <div className="mb-4 rounded-lg border border-border bg-muted/40 p-3">
          <p className="font-medium text-muted-foreground text-xs uppercase tracking-wide">
            Expected at this level
          </p>
          <p className="mt-1 text-sm">{criterion.meetsExpectation}</p>
        </div>
      )}

      {markers.length > 0 && <ScaleComparison markers={markers} />}

      {criterion.answers.length === 0 && criterion.groups.length === 0 ? (
        <p className="text-muted-foreground text-sm">Nothing handed in for this one yet.</p>
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2">
          {criterion.answers.map((answer, index) => {
            const color = relationshipColor(answer.relationshipKey, index);
            const isNa = answer.ordinal === SCALE_NOT_APPLICABLE_ORDINAL;
            return (
              <li
                key={answer.reviewerId ?? `answer-${index}`}
                className="rounded-lg border border-border border-l-4 bg-card p-3 shadow-sm"
                style={{ borderLeftColor: color }}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2 font-semibold text-sm">
                    <span
                      className="size-2.5 rounded-full"
                      style={{ backgroundColor: color }}
                      aria-hidden
                    />
                    {/*
                      Null on the subject's own view. "Rating" rather than a
                      blank: a card with no heading reads as a rendering fault,
                      and the point is that it is deliberate.
                    */}
                    {answer.relationshipName ?? "Rating"}
                    {answer.reviewerName && (
                      <span className="font-normal text-muted-foreground">
                        · {answer.reviewerName}
                      </span>
                    )}
                  </span>
                  {answer.isSensitive && <StatusPill tone="warning" label="Sensitive" />}
                </div>
                <div className="mt-2 flex items-center gap-3">
                  {answer.ordinal !== null && !isNa && (
                    <span
                      className="flex size-10 shrink-0 items-center justify-center rounded-full font-bold text-lg text-white tabular-nums"
                      style={{ backgroundColor: color }}
                    >
                      {answer.ordinal}
                    </span>
                  )}
                  <p className="font-semibold text-base">{answer.rungName ?? "Not rated"}</p>
                </div>
                {answer.comment && (
                  <p className="mt-3 whitespace-pre-line border-border border-l-2 pl-3 text-foreground text-sm">
                    {answer.comment}
                  </p>
                )}
              </li>
            );
          })}
          {/*
            Pooled reviewers — peers, and direct reports once those are
            generated. One card per relationship, never fewer than three people
            behind it, and the count is shown because "3.8 from four people"
            and "3.8 from twelve" are different facts.
          */}
          {criterion.groups.map((group, index) => {
            const color = relationshipColor(
              group.relationshipKey,
              criterion.answers.length + index,
            );
            return (
              <li
                key={group.relationshipKey}
                className="rounded-lg border border-border border-dashed border-l-4 bg-card p-3 shadow-sm"
                style={{ borderLeftColor: color }}
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2 font-semibold text-sm">
                    <span
                      className="size-2.5 rounded-full"
                      style={{ backgroundColor: color }}
                      aria-hidden
                    />
                    {group.relationshipName}
                    <span className="font-normal text-muted-foreground">
                      · {group.count} {group.count === 1 ? "person" : "people"}
                    </span>
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-3">
                  {group.averageOrdinal !== null && (
                    <span
                      className="flex size-10 shrink-0 items-center justify-center rounded-full font-bold text-sm text-white tabular-nums"
                      style={{ backgroundColor: color }}
                    >
                      {group.averageOrdinal.toFixed(1)}
                    </span>
                  )}
                  <p className="font-semibold text-base">
                    {group.averageOrdinal === null ? "Not rated" : "Average"}
                    {group.notApplicableCount > 0 && (
                      <span className="font-normal text-muted-foreground text-xs">
                        {" "}
                        · {group.notApplicableCount} said not applicable
                      </span>
                    )}
                  </p>
                </div>
                {group.comments.map((comment) => (
                  <p
                    key={comment}
                    className="mt-3 whitespace-pre-line border-border border-l-2 pl-3 text-foreground text-sm"
                  >
                    {comment}
                  </p>
                ))}
              </li>
            );
          })}
        </ul>
      )}
    </DetailSection>
  );
}

/**
 * One colour per relationship so "self" and "manager" are told apart at a
 * glance, on the line and on the cards alike.
 *
 * Categorical, not status (see `.claude/rules/design-system.md`): these hues
 * only distinguish who answered — they say nothing good or bad. Self and
 * manager take the two furthest-apart stops of the brand series.
 */
const RELATIONSHIP_COLORS: Record<string, string> = {
  self: "var(--chart-1)",
  manager: "var(--chart-4)",
  peer: "var(--chart-3)",
  direct_report: "var(--chart-5)",
};
const FALLBACK_COLORS = ["var(--chart-1)", "var(--chart-4)", "var(--chart-3)", "var(--chart-5)"];

function relationshipColor(key: string | null, index: number): string {
  return (
    (key ? RELATIONSHIP_COLORS[key] : undefined) ??
    FALLBACK_COLORS[index % FALLBACK_COLORS.length] ??
    "var(--chart-2)"
  );
}

function formatGap(gap: number): string {
  const rounded = Number.isInteger(gap) ? String(gap) : gap.toFixed(1);
  return `${rounded} point${gap === 1 ? "" : "s"} apart`;
}

interface ScaleMarker {
  key: string;
  label: string;
  short: string;
  at: number;
  color: string;
}

/** Where on 1–5 everyone landed, side by side on one line. */
function ScaleComparison({ markers }: { markers: ScaleMarker[] }) {
  const low = 1;
  const high = SCALE_NOT_APPLICABLE_ORDINAL - 1;
  const pct = (at: number) => `${((at - low) / (high - low)) * 100}%`;
  // Markers on the same rung stack upwards rather than hiding each other.
  const seen = new Map<number, number>();
  const placed = markers.map((m) => {
    const slot = seen.get(m.at) ?? 0;
    seen.set(m.at, slot + 1);
    return { ...m, slot };
  });
  const stackHeight = Math.max(...placed.map((m) => m.slot)) + 1;

  return (
    <div className="mb-4 rounded-lg bg-muted/40 px-6 pt-3 pb-2">
      <div className="relative" style={{ height: `${stackHeight * 30 + 14}px` }}>
        <div className="absolute inset-x-0 bottom-1.5 h-1.5 rounded-full bg-border" />
        {Array.from({ length: high - low + 1 }, (_, i) => low + i).map((tick) => (
          <span
            key={tick}
            className="absolute bottom-0 size-4.5 -translate-x-1/2 rounded-full border-2 border-border bg-background"
            style={{ left: pct(tick) }}
            aria-hidden
          />
        ))}
        {placed.map((m) => (
          <span
            key={m.key}
            title={`${m.label}: ${Number.isInteger(m.at) ? m.at : m.at.toFixed(1)}`}
            className="absolute flex size-7 -translate-x-1/2 items-center justify-center rounded-full font-bold text-white text-xs shadow-md ring-2 ring-background"
            style={{ left: pct(m.at), bottom: `${18 + m.slot * 30}px`, backgroundColor: m.color }}
          >
            {m.short}
          </span>
        ))}
      </div>
      <div className="relative mt-1 h-4 text-2xs text-muted-foreground tabular-nums" aria-hidden>
        {Array.from({ length: high - low + 1 }, (_, i) => low + i).map((tick) => (
          <span key={tick} className="absolute -translate-x-1/2" style={{ left: pct(tick) }}>
            {tick}
          </span>
        ))}
      </div>
      <p className="sr-only">
        {markers
          .map((m) => `${m.label} ${Number.isInteger(m.at) ? m.at : m.at.toFixed(1)}`)
          .join(", ")}
      </p>
    </div>
  );
}

/**
 * The report as a file.
 *
 * One row per rating rather than a column per reviewer: reviewers vary per
 * review, and a spreadsheet whose columns move between exports cannot be
 * stacked or filtered.
 *
 * It exports whatever the server sent, so the subject's own download carries
 * the same redaction the page does — pooled reviewers arrive already pooled,
 * and a withheld response was never in the payload to write out. Redaction
 * stays in one place.
 */
function exportReport(report: Report): void {
  const rows: (string | number | null)[][] = [
    ["Criterion", "Reviewer type", "Reviewer", "Rating", "Comment", "Sensitive"],
  ];
  for (const criterion of report.criteria) {
    for (const answer of criterion.answers) {
      rows.push([
        criterion.name,
        answer.relationshipName ?? "",
        answer.reviewerName ?? "",
        answer.rungName ?? "",
        answer.comment ?? "",
        answer.isSensitive ? "yes" : "no",
      ]);
    }
    // One row per pooled group, in the same columns: the reviewer column names
    // the group's size rather than a person, and the rating is the average.
    // A separate sheet would be tidier and would also be a second thing to
    // keep in step with the page.
    for (const group of criterion.groups) {
      rows.push([
        criterion.name,
        group.relationshipName,
        `${group.count} people (pooled)`,
        group.averageOrdinal === null ? "" : group.averageOrdinal.toFixed(1),
        group.comments.join("\n\n"),
        "no",
      ]);
    }
  }
  const safeName = report.subjectName.replace(/[^\w-]+/g, "-").toLowerCase();
  downloadCsv(
    `review-${safeName}-${report.cycleName.replace(/[^\w-]+/g, "-").toLowerCase()}.csv`,
    rows,
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-muted-foreground text-xs uppercase tracking-wide">{label}</dt>
      <dd className="mt-0.5 font-medium">{value}</dd>
    </div>
  );
}
